import React, { ChangeEvent, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

interface RadioOption {
  value: string;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  radioLabel: string;
  radioOptions: Array<RadioOption>;
  defaultRadioValue: string;
  submitButtonLabel: string;
  successMessage: string;
  onSubmit: (file: File, selectedValue: string) => Promise<void>;
}

type SubmissionState = 'idle' | 'success' | 'error';

function GHABulkOperationDialog({
  open,
  onClose,
  title,
  radioLabel,
  radioOptions,
  defaultRadioValue,
  submitButtonLabel,
  successMessage,
  onSubmit,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedValue, setSelectedValue] = useState<string>(defaultRadioValue);
  const [submissionState, setSubmissionState] = useState<SubmissionState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    // Reset state when closing
    setSubmissionState('idle');
    setSelectedFile(null);
    setErrorMessage('');
    setSelectedValue(defaultRadioValue);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const handleReset = () => {
    setSubmissionState('idle');
    setSelectedFile(null);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const { handleSubmit } = useForm();

  const handleFileUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar('No file selected', { variant: 'error' });
      return;
    }

    try {
      await onSubmit(selectedFile, selectedValue);
      setSubmissionState('success');
      enqueueSnackbar(successMessage, { variant: 'success' });
    } catch (error) {
      setSubmissionState('error');
      const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred';
      setErrorMessage(errorMsg);
      enqueueSnackbar('Submission failed', { variant: 'error' });
      console.error('Failed to submit bulk operation', error);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit(handleFileUpload)}
      component="form"
    >
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {submissionState === 'success' && (
            <Alert severity="success">
              {successMessage} Please refresh the page in a few moments to see the updates.
            </Alert>
          )}
          {submissionState === 'error' && (
            <Alert severity="error">
              <Typography variant="body2" component="div">
                <strong>Error:</strong> {errorMessage}
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                Please try again and make sure your CSV format follows the example below.
              </Typography>
            </Alert>
          )}
          <Box>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}
                >
                  Example CSV format:
                </Typography>
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper'),
                  borderRadius: 1,
                  overflow: 'auto',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                  propertyIds
                </Box>
                {'\n'}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  property-123
                </Box>
                {'\n'}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  property-456
                </Box>
                {'\n'}
                <Box component="span" sx={{ color: 'text.primary' }}>
                  property-789
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
                • First row must be the column header: <strong>propertyIds</strong>
                <br />• Each subsequent row should contain one property ID
              </Typography>
            </Paper>
          </Box>

          <FormControl component="fieldset">
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              {radioLabel}
            </Typography>
            <RadioGroup row value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)} sx={{ mt: 1 }}>
              {radioOptions.map((option, index) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                  sx={{ mr: index < radioOptions.length - 1 ? 3 : 0 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1.5 }}>
              Upload CSV File
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                disabled={!!selectedFile}
                startIcon={selectedFile ? <CheckBoxIcon /> : <CloudUploadIcon />}
              >
                {selectedFile ? 'File Uploaded' : 'Choose File'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv, text/csv"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedFile(event.target.files?.[0] || null)}
                  style={{
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    height: 1,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    width: 1,
                  }}
                />
              </Button>
              {selectedFile && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {submissionState === 'success' ? (
          <Button variant="contained" onClick={handleClose} color="primary">
            Close
          </Button>
        ) : submissionState === 'error' ? (
          <>
            <Button variant="text" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleReset} color="primary">
              Try Again
            </Button>
          </>
        ) : (
          <>
            <Button variant="text" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={!selectedFile} color="primary">
              {submitButtonLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default GHABulkOperationDialog;
